// src/lib/analytics/attribution.ts
/**
 * @fileoverview First-party acquisition attribution (first-touch / last-touch /
 * internal referral).
 * @description Captures and persists *where a visitor came from* so it can be
 * attached as custom properties to the conversion events — `request_access_submit`,
 * `purchase`, `begin_checkout`, `view_item` — and answered directly in both GA4
 * and the Vercel dashboard (which, unlike GA4, does no session attribution of
 * its own on custom events).
 *
 * Three things are tracked, because they answer different questions:
 *
 * 1. **First touch** (`first_touch_*`, `first_landing_path`) — the campaign /
 *    referrer / landing page that *first* brought this browser to the site. Set
 *    once and never overwritten, so it survives the Stripe round-trip and any
 *    number of return visits — the value GA4's "last non-direct" model throws
 *    away.
 * 2. **Last touch** (`last_touch_*`) — the most recent campaign / external
 *    referrer entry before the conversion. Updated only on a genuinely new
 *    campaign/referral entry (not on internal navigation or a same-campaign
 *    refresh), so `attribution_touch_count` is a meaningful multi-touch count.
 * 3. **Internal referral** (`referrer_path`) — the on-site page the visitor was
 *    on immediately *before* the page where the conversion fired (blog post,
 *    glossary term, resource, …). This is the "where on the site did this
 *    request-access / framework referral come from" signal the rest of the
 *    layer couldn't answer.
 *
 * Design constraints (match the rest of the analytics layer):
 * - **Client-only.** Every primitive no-ops when `window` is absent.
 * - **Consent-gated writes (region-aware).** Persisting acquisition context is
 *   `analytics_storage`, so {@link recordAttributionTouch} writes nothing unless
 *   `isAnalyticsAllowed()` (rest-of-world opt-out) AND — in a consent-required
 *   region (EEA/UK/CH, detected via the middleware region cookie) — an EXPLICIT
 *   opt-in exists. So a denied-by-default EEA visitor stores nothing pre-consent.
 *   Reads (`getAttributionProperties`) are unguarded — they only ever run while
 *   merging into an event that is *itself* consent-gated, and return `{}` when no
 *   data was stored (i.e. when the visitor was opted out / not yet opted in).
 * - **Never throws.** Storage can be unavailable (private mode, quota); every
 *   access is wrapped.
 * - **Cheap.** Per pageview: one `URLSearchParams` parse, one localStorage read
 *   (write only on a first/new touch), and one sessionStorage read+write for the
 *   nav pair — all synchronous, microsecond-scale. No timers, listeners, or
 *   observers; `recordAttributionTouch` is called from the existing per-route
 *   `Analytics` effect.
 *
 * Known limitation (Safari ITP): non-cookie storage (localStorage) is capped at
 * ~7 days of inactivity on Safari/WebKit (and JS `document.cookie` is likewise
 * 7-day-capped), so a first touch can lapse for a long-dormant Safari visitor —
 * the conversion then attributes to the next observed touch. Surviving that
 * needs a server-set first-party cookie (Set-Cookie, up to 400 days), which is
 * implemented via `adsights_attr_v1` (see `attribution-cookie.ts` + middleware).
 * The client capture is the right default; GA4's own session attribution remains
 * the backstop.
 */

import {
  hasExplicitAnalyticsOptIn,
  hasExplicitAnalyticsOptOut,
  isAnalyticsAllowed,
  readAnalyticsGrantFromCookies,
  CONSENT_STORAGE_KEY,
} from "./consent"
import {
  ANALYTICS_COOKIE_MAX_AGE_SEC,
  ATTRIBUTION_COOKIE_NAME,
  ATTRIBUTION_NAV_KEY,
  ATTRIBUTION_STORAGE_KEY,
  CONSENT_ANALYTICS_EXPLICIT_COOKIE,
} from "./cookie-names"
import {
  parseAttributionCookie,
  serializeAttributionCookie,
  type CookieAttribution,
  type CookieTouch,
} from "./attribution-cookie"
import { detectInAppBrowser } from "./in-app-browser"
import { REGION_CONSENT_REQUIRED_COOKIE } from "./region"

export { ATTRIBUTION_NAV_KEY, ATTRIBUTION_STORAGE_KEY } from "./cookie-names"

/** Flat property bag GA4 / Vercel accept (no nested objects/arrays). */
export type AttributionProps = Record<string, string | number>

/** Sentinel source/medium for a visit with no campaign and no external referrer. */
const DIRECT_SOURCE = "(direct)"
const NONE_MEDIUM = "(none)"

/**
 * GA4 caps an event-parameter VALUE at 100 chars; keep every persisted/emitted
 * string within that so a pathological referrer/landing URL can't bloat storage
 * or get silently truncated downstream.
 */
const MAX_PROP_LEN = 100

/**
 * Slice to at most `max` UTF-16 code units WITHOUT leaving a dangling lone
 * surrogate. A naive `.slice(0, max)` can cut an emoji / astral-plane char (a
 * surrogate PAIR) in half, leaving an unpaired high surrogate that a JSON / GA4 /
 * Zod sink may mangle or reject — which would drop the very lead/event this
 * best-effort metadata is attached to. So if the cut lands on a high surrogate
 * (U+D800–U+DBFF, the leading half of a pair), drop it.
 */
function truncateChars(value: string, max: number): string {
  if (value.length <= max) return value
  const out = value.slice(0, max)
  const last = out.charCodeAt(out.length - 1)
  return last >= 0xd800 && last <= 0xdbff ? out.slice(0, -1) : out
}

/** Trim + clamp a captured string to GA4's value limit. */
function clamp(value: string): string {
  return truncateChars(value.trim(), MAX_PROP_LEN)
}

import {
  CLICK_ID_CHANNEL,
  CLICK_ID_PARAMS,
  classifyReferrerHost,
  isNonAcquisitionReferrerHost,
} from "./click-id-channel"

/** A single acquisition touch (first or last). */
interface Touch {
  source: string
  medium: string
  campaign?: string
  term?: string
  content?: string
  /** External referrer hostname (www-stripped) for a referral/organic/social touch. */
  referrer?: string
  /** Path (no query) of the page this touch landed on. */
  landing_path: string
  /** Click identifier value, when one was present. */
  click_id?: string
  /** Which click-id param it was (e.g. `gclid`). */
  click_id_type?: string
  /** Epoch ms of the touch. */
  at: number
}

/** The in-session nav record (sessionStorage): the `{current, previous}` path
 *  pair for internal-referral, plus the last external referrer consumed as an
 *  acquisition touch (so a stale SPA-navigation referrer isn't re-counted). */
interface NavRecord {
  current?: string
  previous?: string | null
  consumedRef?: string
}

/** The persisted attribution record. */
interface StoredAttribution {
  first: Touch
  last: Touch
  /** Epoch ms of the very first touch (basis for `days_since_first_touch`). */
  first_seen_at: number
  /** Count of distinct campaign / external-referral entries (multi-touch depth). */
  touch_count: number
}

//=============================================================================
// Site-section derivation (for "which section drives conversions" analysis)
//=============================================================================

/**
 * Map a route pathname to a coarse **site section** — the unit of "site section
 * strength" analysis (which part of the site acquires/refers conversions). Kept
 * deliberately coarse and stable (one label per top-level area, with the
 * high-value resource sub-areas split out) so it groups cleanly in GA4/Vercel/
 * BigQuery and is cheap to derive. Exported for reuse + tests.
 */
export function sectionForPath(path: string | undefined): string {
  if (!path || path === "/") return "home"
  const segments = path.replace(/^\/+/, "").split("/")
  const top = segments[0] ?? ""
  switch (top) {
    case "":
      return "home"
    case "blog":
      return "blog"
    case "resources": {
      const sub = segments[1]
      // Split the high-traffic resource sub-areas; collapse the rest to their sub.
      if (sub === "glossary") return "resources/glossary"
      if (sub === "tools") return "resources/tools"
      return sub ? `resources/${sub}` : "resources"
    }
    case "frameworks":
      return "frameworks"
    case "pricing":
      return "pricing"
    case "about":
      return "about"
    case "request-access":
      return "request-access"
    case "checkout":
      return "checkout"
    case "account":
      return "account"
    default:
      return top || "other"
  }
}

//=============================================================================
// Referrer classification
//=============================================================================

/**
 * Referrer hosts that are transactional/auth redirects, never an acquisition
 * source — e.g. returning from Stripe Checkout onto `/checkout/success`.
 * @see {@link isNonAcquisitionReferrerHost}
 */

/** External referrer hostname (www-stripped), or `undefined` for same-origin/none/transactional. */
function externalReferrerHost(): string | undefined {
  if (typeof document === "undefined" || !document.referrer) return undefined
  try {
    const u = new URL(document.referrer)
    const host = u.hostname.replace(/^www\./, "")
    // Compare www-stripped on BOTH sides so the apex→www production redirect
    // (adsights.ai → www.adsights.ai) isn't misread as an external referral.
    const selfHost = window.location.hostname.replace(/^www\./, "")
    if (!host || host === selfHost) return undefined
    // A payment/auth redirect is not where the visitor "came from".
    if (isNonAcquisitionReferrerHost(host)) {
      return undefined
    }
    return host
  } catch {
    // Unparseable referrer → treat as none.
  }
  return undefined
}

//=============================================================================
// Touch construction
//=============================================================================

/**
 * Build the touch for the current pageview and whether it counts as a new
 * acquisition touch (a campaign, a click id, or an external referrer — but NOT
 * a direct load or internal navigation).
 */
function readCurrentTouch(
  path: string,
  refHost: string | undefined,
): { touch: Touch; isAcquisition: boolean } {
  // Lowercase the param keys once so both utm_* and click ids are matched
  // case-insensitively (platforms vary: `ScCid`, `UTM_Source`, …).
  const lc = new Map<string, string>()
  for (const [k, v] of new URLSearchParams(window.location.search).entries()) {
    const trimmed = v.trim()
    if (trimmed) lc.set(k.toLowerCase(), trimmed)
  }
  const utm = (k: string): string | undefined => lc.get(`utm_${k}`)

  const utmSource = utm("source")
  const utmMedium = utm("medium")
  const campaign = utm("campaign")
  const term = utm("term")
  const content = utm("content")

  let clickId: string | undefined
  let clickIdType: string | undefined
  for (const k of CLICK_ID_PARAMS) {
    const v = lc.get(k)
    if (v) {
      clickId = v
      clickIdType = k
      break
    }
  }

  // `refHost` is the already-resolved external referrer to attribute to (or
  // `undefined` to ignore it) — the caller decides freshness (see
  // `recordAttributionTouch`), because `document.referrer` is stale across SPA
  // navigations but live on every fresh document load. UTM / click-id params
  // live in the live URL, so they're honored on any pageview regardless.
  let source: string
  let medium: string
  let isAcquisition: boolean

  if (utmSource || utmMedium || campaign) {
    // Explicit campaign tagging always wins.
    isAcquisition = true
    const clickChannel = clickIdType ? CLICK_ID_CHANNEL[clickIdType] : undefined
    const refChannel = refHost ? classifyReferrerHost(refHost) : undefined
    source = utmSource ?? clickChannel?.source ?? refChannel?.source ?? "(referral)"
    medium = utmMedium ?? clickChannel?.medium ?? refChannel?.medium ?? NONE_MEDIUM
  } else if (clickIdType) {
    // Bare click id (no utm) → infer the channel from the network.
    isAcquisition = true
    const channel = CLICK_ID_CHANNEL[clickIdType]
    source = channel?.source ?? "(referral)"
    medium = channel?.medium ?? "cpc"
  } else if (refHost) {
    // External referrer (no utm, no click id) → organic / social / referral.
    isAcquisition = true
    const channel = classifyReferrerHost(refHost)
    source = channel.source
    medium = channel.medium
  } else {
    // Direct / typed-in / internal navigation.
    isAcquisition = false
    source = DIRECT_SOURCE
    medium = NONE_MEDIUM
  }

  const touch: Touch = {
    source: clamp(source),
    medium: clamp(medium),
    landing_path: clamp(path),
    at: Date.now(),
  }
  if (campaign) touch.campaign = clamp(campaign)
  if (term) touch.term = clamp(term)
  if (content) touch.content = clamp(content)
  if (refHost) touch.referrer = clamp(refHost)
  if (clickId) {
    touch.click_id = clamp(clickId)
    touch.click_id_type = clickIdType
  }
  return { touch, isAcquisition }
}

/** Two touches share an acquisition identity (so a refresh isn't a new touch). */
function sameTouch(a: Touch, b: Touch): boolean {
  return (
    a.source === b.source &&
    a.medium === b.medium &&
    (a.campaign ?? "") === (b.campaign ?? "") &&
    // Creative/keyword variants share source/medium/campaign but differ by
    // utm_content (creative) or utm_term (keyword). They ARE distinct touches —
    // `readCurrentTouch` captures both and `touchProps` emits them — so include
    // them here, or a second variant click would be deduped as a refresh and the
    // conversion would keep stale creative/term attribution.
    (a.content ?? "") === (b.content ?? "") &&
    (a.term ?? "") === (b.term ?? "") &&
    // Distinct ad clicks (e.g. ?gclid=A then ?gclid=B) normalize to the same
    // source/medium with no campaign — include the click id so a genuine second
    // paid click counts as a new touch instead of being deduped as a refresh.
    (a.click_id ?? "") === (b.click_id ?? "")
  )
}

//=============================================================================
// Persistence
//=============================================================================

/** Optional string fields on a stored touch, sanitized when reading. */
const OPTIONAL_TOUCH_STRING_FIELDS = [
  "campaign",
  "term",
  "content",
  "referrer",
  "click_id",
  "click_id_type",
] as const

/**
 * Rebuild a clean {@link Touch} from a stored value, or `null` if its REQUIRED
 * fields are missing/mis-typed. A corrupt record (extension / manual storage
 * edit) must never reach an event: a wrong-typed REQUIRED field voids the whole
 * touch, a wrong-typed OPTIONAL field (e.g. `campaign: 123`) is DROPPED, and
 * every surviving string is re-`clamp`ed to the GA4/schema value bound — so an
 * oversized stored value (corrupt store, or a future buggy build that skipped
 * the capture-time clamp) can't make `touchProps()` emit an over-length prop
 * that Zod then rejects, blocking the whole lead even though attribution is
 * best-effort.
 */
function sanitizeStoredTouch(t: unknown): Touch | null {
  if (!t || typeof t !== "object") return null
  const o = t as Record<string, unknown>
  if (
    typeof o.source !== "string" ||
    typeof o.medium !== "string" ||
    typeof o.landing_path !== "string" ||
    typeof o.at !== "number" ||
    !Number.isFinite(o.at)
  ) {
    return null
  }
  const touch: Touch = {
    source: clamp(o.source),
    medium: clamp(o.medium),
    landing_path: clamp(o.landing_path),
    at: o.at,
  }
  for (const field of OPTIONAL_TOUCH_STRING_FIELDS) {
    const value = o[field]
    if (typeof value === "string") touch[field] = clamp(value)
  }
  return touch
}

function readCookieValue(name: string): string | undefined {
  if (typeof document === "undefined") return undefined
  try {
    const parts = document.cookie.split(";")
    for (const part of parts) {
      const [k, ...rest] = part.trim().split("=")
      if (k === name) return rest.join("=")
    }
  } catch {
    return undefined
  }
  return undefined
}

function writeAttributionCookie(stored: StoredAttribution): void {
  if (typeof document === "undefined") return
  // Preserve a server Set-Cookie (up to ~400 days on Safari ITP). A JS
  // document.cookie write is subject to the ~7-day cap and would downgrade it.
  if (readCookieValue(ATTRIBUTION_COOKIE_NAME)) return
  try {
    const toCookieTouch = (touch: Touch): CookieTouch => ({
      s: touch.source,
      m: touch.medium,
      lp: touch.landing_path,
      at: touch.at,
      ...(touch.campaign ? { c: touch.campaign } : {}),
      ...(touch.referrer ? { r: touch.referrer } : {}),
      ...(touch.term ? { t: touch.term } : {}),
      ...(touch.content ? { co: touch.content } : {}),
      ...(touch.click_id ? { ci: touch.click_id } : {}),
      ...(touch.click_id_type ? { cit: touch.click_id_type } : {}),
    })
    const payload: CookieAttribution = {
      fs: stored.first_seen_at,
      tc: stored.touch_count,
      f: toCookieTouch(stored.first),
      l: toCookieTouch(stored.last),
    }
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(serializeAttributionCookie(payload))}; Path=/; Max-Age=${ANALYTICS_COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`
  } catch {
    // ignore
  }
}

function cookieToStored(parsed: CookieAttribution): StoredAttribution | null {
  const toTouch = (t: CookieTouch): Touch | null =>
    sanitizeStoredTouch({
      source: t.s,
      medium: t.m,
      landing_path: t.lp,
      at: t.at,
      ...(t.c ? { campaign: t.c } : {}),
      ...(t.r ? { referrer: t.r } : {}),
      ...(t.t ? { term: t.t } : {}),
      ...(t.co ? { content: t.co } : {}),
      ...(t.ci ? { click_id: t.ci } : {}),
      ...(t.cit ? { click_id_type: t.cit } : {}),
    })

  const first = toTouch(parsed.f)
  const last = toTouch(parsed.l)
  if (!first || !last) return null

  return {
    first,
    last,
    first_seen_at: parsed.fs,
    touch_count: parsed.tc,
  }
}

function readStoredFromCookie(): StoredAttribution | null {
  const parsed = parseAttributionCookie(readCookieValue(ATTRIBUTION_COOKIE_NAME))
  return parsed ? cookieToStored(parsed) : null
}

function isDirectTouch(touch: Touch): boolean {
  return touch.source === DIRECT_SOURCE && touch.medium === NONE_MEDIUM
}

function touchRichnessScore(touch: Touch): number {
  let score = 0
  if (!isDirectTouch(touch) && touch.source !== "(referral)") score += 10
  if (touch.medium !== NONE_MEDIUM) score += 5
  if (touch.campaign) score += 3
  if (touch.click_id) score += 2
  if (touch.medium === "in_app_social") score += 8
  return score
}

function mergeStoredRecords(a: StoredAttribution, b: StoredAttribution): StoredAttribution {
  const pickFirstRecord = (): StoredAttribution => {
    const aDirect = isDirectTouch(a.first)
    const bDirect = isDirectTouch(b.first)
    if (aDirect !== bDirect) return aDirect ? b : a
    const scoreDiff = touchRichnessScore(b.first) - touchRichnessScore(a.first)
    if (scoreDiff > 0) return b
    if (scoreDiff < 0) return a
    return a.first_seen_at <= b.first_seen_at ? a : b
  }

  const firstRecord = pickFirstRecord()
  const last = a.last.at >= b.last.at ? a.last : b.last
  return {
    first: firstRecord.first,
    last,
    first_seen_at: Math.min(a.first_seen_at, b.first_seen_at),
    touch_count: Math.max(a.touch_count, b.touch_count),
  }
}

/**
 * Whether an explicit analytics grant exists for attribution reads (localStorage
 * or the edge-readable explicit cookie when storage is unavailable).
 *
 * @returns {boolean} True when analytics was explicitly opted in.
 */
function hasExplicitAnalyticsGrantForReads(): boolean {
  if (hasExplicitAnalyticsOptIn()) return true
  if (typeof document === "undefined") return false
  try {
    return document.cookie.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=1`)
  } catch {
    return false
  }
}

function attributionStorageReadable(): boolean {
  if (typeof window === "undefined") return true
  if (hasExplicitAnalyticsOptOut()) return false

  let explicitGrant = false
  let explicitDeny = false
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) {
      const prefs = JSON.parse(raw) as { analytics?: boolean }
      if (prefs.analytics === true) explicitGrant = true
      if (prefs.analytics === false) explicitDeny = true
    }
  } catch {
    // Storage unavailable — treat as no stored preference, not an explicit denial.
  }

  if (explicitDeny) return false
  if (explicitGrant) return true

  const cookieGrant = readAnalyticsGrantFromCookies()
  if (cookieGrant === false) return false
  if (cookieGrant === true) return true

  if (isConsentRequiredRegionClient() && !hasExplicitAnalyticsGrantForReads()) return false

  // GPC is a sale/share opt-out, not an opt-out of first-party measurement, so
  // it does not block reading our own first-party attribution.
  return true
}

function readStored(): StoredAttribution | null {
  if (!attributionStorageReadable()) return null

  let fromLocal: StoredAttribution | null = null
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const first = sanitizeStoredTouch(parsed?.first)
      const last = sanitizeStoredTouch(parsed?.last)
      if (
        first &&
        last &&
        typeof parsed.first_seen_at === "number" &&
        Number.isFinite(parsed.first_seen_at) &&
        typeof parsed.touch_count === "number" &&
        Number.isFinite(parsed.touch_count)
      ) {
        fromLocal = {
          first,
          last,
          first_seen_at: parsed.first_seen_at,
          touch_count: parsed.touch_count,
        }
      }
    }
  } catch {
    fromLocal = null
  }

  const fromCookie = readStoredFromCookie()
  if (fromLocal && fromCookie) return mergeStoredRecords(fromLocal, fromCookie)
  return fromLocal ?? fromCookie
}

function writeStored(value: StoredAttribution): void {
  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Quota/unavailable → drop silently (analytics must never break UX).
  }
  writeAttributionCookie(value)
}

/**
 * Same-site `document.referrer` PATH (query-stripped), or `undefined` when the
 * referrer is absent, cross-site, or unparseable.
 *
 * Seeds the internal referrer on an ENTRY pageview opened in a NEW TAB from an
 * on-site CTA: a new tab starts with empty `sessionStorage` (so there's no nav
 * pair to source `referrer_path` from), yet `document.referrer` still holds the
 * same-site page that opened it. `externalReferrerHost()` deliberately discards
 * same-site referrers (they're not acquisition), so without this seed those
 * common new-tab conversions would arrive with no `referrer_path`/
 * `referrer_section` and undercount section-strength analysis.
 */
function sameSiteReferrerPath(): string | undefined {
  if (typeof document === "undefined" || !document.referrer) return undefined
  try {
    const u = new URL(document.referrer)
    const host = u.hostname.replace(/^www\./, "")
    const selfHost = window.location.hostname.replace(/^www\./, "")
    if (host !== selfHost) return undefined
    return u.pathname
  } catch {
    return undefined
  }
}

/**
 * Update the in-session `{ current, previous }` path pair and return the
 * previous on-site path (the internal referrer for the current page).
 */
function updateNavPath(path: string): string | undefined {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_NAV_KEY)
    const record = raw ? (JSON.parse(raw) as NavRecord) : {}
    const current = record.current
    if (current === path) {
      // Same page re-render (no navigation) — keep the existing previous.
      return record.previous ?? undefined
    }
    // On a true ENTRY pageview (no prior `current`), seed the previous from a
    // same-site referrer so a new-tab CTA open still records its internal
    // referrer; on any later navigation `current` is the previous page.
    const nextPrevious = current ?? sameSiteReferrerPath()
    // Preserve sibling fields (e.g. `consumedRef`) when rewriting the record.
    sessionStorage.setItem(
      ATTRIBUTION_NAV_KEY,
      JSON.stringify({ ...record, current: path, previous: nextPrevious ?? null }),
    )
    return nextPrevious
  } catch {
    return undefined
  }
}

/**
 * The external referrer host this session has already counted as an acquisition
 * touch. `document.referrer` is fixed for a document's lifetime, so it's stale
 * across SPA route changes but changes on a fresh document load (incl. a same-tab
 * return from an external site). Tracking the last-consumed value lets
 * `recordAttributionTouch` tell a genuinely-new referrer from a stale one without
 * fragile load-counting state.
 */
function readConsumedReferrer(): string | undefined {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_NAV_KEY)
    if (!raw) return undefined
    return (JSON.parse(raw) as NavRecord).consumedRef ?? undefined
  } catch {
    return undefined
  }
}

function writeConsumedReferrer(host: string): void {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_NAV_KEY)
    const record = raw ? (JSON.parse(raw) as NavRecord) : {}
    record.consumedRef = host
    sessionStorage.setItem(ATTRIBUTION_NAV_KEY, JSON.stringify(record))
  } catch {
    // ignore
  }
}

/**
 * Whether the current pageview is the **entry** pageview of this tab/session —
 * i.e. no on-site path has been recorded yet. The entry pageview is the only one
 * whose `document.referrer` is meaningful (a fresh document load); a new tab gets
 * a fresh `sessionStorage`, so an absent `current` is the robust signal.
 */
function isEntryPageview(): boolean {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_NAV_KEY)
    if (!raw) return true
    const parsed = JSON.parse(raw) as { current?: string }
    return !parsed.current
  } catch {
    return true
  }
}

/**
 * Whether THIS request's geo is a consent-required (EEA/UK/CH) region, read with
 * NO network round-trip from the `REGION_CONSENT_REQUIRED_COOKIE` the edge
 * middleware stamps ("1"/"0"). Absent cookie (rest-of-world default, local dev,
 * or before the first response sets it) → `false`, preserving the opt-out
 * posture for everyone outside the default-deny regions.
 */
function isConsentRequiredRegionClient(): boolean {
  if (typeof document === "undefined") return false
  try {
    // Cheap substring scan — avoids a regex per pageview on the hot nav path.
    return document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`)
  } catch {
    return false
  }
}

/**
 * Whether attribution may be PERSISTED to client storage right now.
 *
 * - Rest of world: the existing opt-out gate (`isAnalyticsAllowed` — false only
 *   on an explicit opt-out; GPC is a sale/share opt-out, not a first-party
 *   analytics opt-out, so it does not gate this).
 * - EEA/UK/CH (consent required, per the middleware region cookie): persisting
 *   first-party acquisition data is `analytics_storage`, so it requires an
 *   EXPLICIT opt-in — the Consent-Mode default-deny parity for this storage
 *   surface. Without it, a first-time denied-by-default visitor would otherwise
 *   write attribution before any opt-in. Zero client cost: one cookie + one
 *   localStorage read, both synchronous.
 */
function isAttributionStorageAllowed(): boolean {
  if (!isAnalyticsAllowed()) return false
  if (isConsentRequiredRegionClient() && !hasExplicitAnalyticsOptIn()) return false
  return true
}

/** Read the internal referrer (previous on-site path this session) without mutating. */
function readNavPrevious(): string | undefined {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_NAV_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as { previous?: string | null; current?: string }
    const prev = parsed.previous ?? undefined
    // Don't report the current page as its own referrer.
    return prev && prev !== parsed.current ? prev : undefined
  } catch {
    return undefined
  }
}

//=============================================================================
// Public API
//=============================================================================

/**
 * Record one pageview's acquisition context. Called once per App Router
 * navigation from `components/Analytics.tsx` (alongside `pageview()`).
 *
 * - Sets first-touch exactly once (even for a direct landing, so the landing
 *   path is always captured).
 * - Updates last-touch only on a genuinely new acquisition touch (campaign,
 *   click id, or external referrer that differs from the stored last touch),
 *   incrementing `touch_count`.
 * - Always advances the in-session nav pair so `referrer_path` is available.
 *
 * No-op (and persists nothing) when analytics consent is absent — acquisition
 * storage is `analytics_storage`.
 *
 * @param path Current route pathname (no query), from `usePathname()`.
 */
/**
 * Whether the middleware/API region cookie is present (inline to avoid importing
 * consent-banner-visibility, which would cycle through consent-preferences).
 *
 * @returns {boolean} True when `adsights_region_cr` is set.
 */
function hasRegionConsentCookieForHydration(): boolean {
  if (typeof document === "undefined") return false
  try {
    return (
      document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`) ||
      document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=0`)
    )
  } catch {
    return false
  }
}

export async function recordAttributionTouchAsync(path: string): Promise<void> {
  if (typeof window === "undefined") return
  if (!hasRegionConsentCookieForHydration()) {
    const { hydrateRegionCookieIfMissing } = await import("./hydrate-region-cookie")
    const outcome = await hydrateRegionCookieIfMissing()
    // Block the future WRITE only when hydration *confirmed* a consent-required
    // region without explicit opt-in (the EEA/UK/CH consent-for-storage gate).
    // Unresolved geo / fetch failure → proceed under the RoW default (no region
    // cookie ⇒ not consent-required in the sync gate). We do NOT purge data
    // already written under the RoW default before hydration resolved — no law
    // requires retroactively deleting already-stored first-party data, and the
    // site's posture is to retain first-party data unless collection is barred.
    if (outcome.consentRequired && !hasExplicitAnalyticsOptIn()) {
      return
    }
  }
  recordAttributionTouch(path)
}

/** Max wait for attribution hydration on the checkout metadata path. */
export const CHECKOUT_ATTRIBUTION_BUDGET_MS = 2000

/**
 * {@link recordAttributionTouchAsync} with a bounded wait so commerce flows
 * never block indefinitely on region-signal latency.
 *
 * @param path Current route pathname.
 * @param budgetMs Max milliseconds to wait (default {@link CHECKOUT_ATTRIBUTION_BUDGET_MS}).
 * @returns {Promise<void>} Resolves when the touch completes or the budget elapses.
 */
export async function recordAttributionTouchAsyncBounded(
  path: string,
  budgetMs: number = CHECKOUT_ATTRIBUTION_BUDGET_MS,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      recordAttributionTouchAsync(path),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, budgetMs)
      }),
    ])
  } catch {
    // best-effort
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * Record one pageview's acquisition context synchronously.
 *
 * Prefer {@link recordAttributionTouchAsync} at every entry point so the EEA
 * region gate sees a hydrated `adsights_region_cr` cookie before persisting.
 *
 * @param path Current route pathname (no query), from `usePathname()`.
 */
export function recordAttributionTouch(path: string): void {
  if (typeof window === "undefined") return
  // All persistence below is `analytics_storage` — including the in-session nav
  // pair — so an opted-out visitor stores nothing and never retains pre-consent
  // navigation history. In a consent-required region (EEA/UK/CH) this also
  // requires an explicit opt-in, not just the rest-of-world opt-out default.
  if (!isAttributionStorageAllowed()) return

  // Capture entry-ness BEFORE advancing the nav pair (which sets `current`).
  const entry = isEntryPageview()

  // Decide whether the live `document.referrer` is a NEW acquisition touch.
  // `document.referrer` is fixed for a document's whole lifetime — stale across
  // SPA route changes, but LIVE on every fresh document load, INCLUDING a
  // same-tab return from an external site (where `sessionStorage` persists, so
  // `entry` alone would miss it). The robust signal is the referrer VALUE:
  // consult it on the session entry OR whenever the live external referrer
  // differs from the one already consumed this session (a CHANGED referrer ⇒ a
  // real document navigation, never an SPA route change — which keeps the same
  // stale value). This counts the same-tab organic/referral return that the
  // entry-only check dropped, without fragile per-load state.
  const liveRefHost = externalReferrerHost()
  const referrerIsNew = !!liveRefHost && liveRefHost !== readConsumedReferrer()
  const refHost = entry || referrerIsNew ? liveRefHost : undefined

  // Advance the in-session nav pair so `referrer_path` is available on a later
  // conversion (it's the on-site page that referred this one), and remember the
  // consumed external referrer so the next SPA navigation (same stale value)
  // isn't re-counted.
  updateNavPath(path)
  if (liveRefHost) writeConsumedReferrer(liveRefHost)

  const { touch: baseTouch, isAcquisition: baseAcquisition } = readCurrentTouch(path, refHost)
  let touch = baseTouch
  let isAcquisition = baseAcquisition
  const stored = readStored()

  const inApp = detectInAppBrowser()
  const inAppUpgrade =
    inApp.isInApp && inApp.app && !isAcquisition && entry
      ? {
          source: inApp.app,
          medium: "in_app_social" as const,
        }
      : null

  if (inAppUpgrade) {
    touch = {
      ...touch,
      source: inAppUpgrade.source,
      medium: inAppUpgrade.medium,
      at: Date.now(),
    }
    isAcquisition = true
  }

  if (!stored) {
    writeStored({
      first: touch,
      last: touch,
      first_seen_at: touch.at,
      touch_count: isAcquisition ? 1 : 0,
    })
    return
  }

  if (inAppUpgrade && isDirectTouch(stored.first)) {
    const lastChanged = !sameTouch(touch, stored.last)
    writeStored({
      first: touch,
      last: touch,
      first_seen_at: stored.first_seen_at,
      touch_count: lastChanged
        ? stored.touch_count + 1
        : Math.max(stored.touch_count, 1),
    })
    return
  }

  // Only a NEW acquisition touch updates last-touch / increments the count;
  // direct loads and internal navigation preserve the existing record.
  if (isAcquisition && !sameTouch(touch, stored.last)) {
    writeStored({
      first: stored.first,
      last: touch,
      first_seen_at: stored.first_seen_at,
      touch_count: stored.touch_count + 1,
    })
  }
}

/**
 * Flatten one stored touch into `{prefix}_*` event properties (only set keys).
 */
function touchProps(touch: Touch, prefix: "first" | "last"): AttributionProps {
  const out: AttributionProps = {
    [`${prefix}_touch_source`]: touch.source,
    [`${prefix}_touch_medium`]: touch.medium,
  }
  if (touch.campaign) out[`${prefix}_touch_campaign`] = touch.campaign
  if (touch.term) out[`${prefix}_touch_term`] = touch.term
  if (touch.content) out[`${prefix}_touch_content`] = touch.content
  if (touch.referrer) out[`${prefix}_touch_referrer`] = touch.referrer
  return out
}

/**
 * The acquisition + internal-referral properties to merge into a conversion
 * event. Returns `{}` when nothing was stored (no window, storage unavailable,
 * or the visitor was opted out so `recordAttributionTouch` never wrote).
 *
 * Shape (all keys optional):
 * - `first_touch_source` / `_medium` / `_campaign` / `_term` / `_content` / `_referrer`
 * - `first_landing_path`
 * - `last_touch_source` / `_medium` / `_campaign` / `_term` / `_content` / `_referrer`
 * - `attribution_touch_count` — distinct campaign/referral entries
 * - `days_since_first_touch` — whole days between first touch and now
 * - `referrer_path` — the on-site page visited immediately before this one
 */
export function getAttributionProperties(): AttributionProps {
  if (typeof window === "undefined") return {}
  // Reads are intentionally NOT gated on `isAnalyticsAllowed()` (which would also
  // block the rest-of-world opt-out DEFAULT and GPC, over-restricting beyond what
  // a voluntarily-submitted lead's first-party CRM attribution requires). But an
  // EXPLICIT opt-out is a withdrawal that must be honored in EVERY tab: the
  // privacy UI purges this tab's stores, and shared `localStorage` is gone for
  // all tabs, yet each OTHER open tab keeps its own `sessionStorage` nav pair —
  // which `readNavPrevious()` below would still surface (e.g. attaching
  // `referrer_path` to a HubSpot lead submitted in that tab after opt-out). So
  // when an explicit opt-out is stored, purge this tab's residual nav pair and
  // return nothing. (After opt-out the cross-session record is already empty.)
  if (hasExplicitAnalyticsOptOut()) {
    clearStoredAttribution()
    return {}
  }
  if (!attributionStorageReadable()) {
    if (readAnalyticsGrantFromCookies() === false) {
      clearStoredAttribution()
    }
    return {}
  }
  const props: AttributionProps = {}

  const stored = readStored()
  if (stored) {
    Object.assign(props, touchProps(stored.first, "first"))
    props.first_landing_path = stored.first.landing_path
    // Site section the visitor first entered through — the basis for
    // "which section acquires conversions" (site-section strength) analysis.
    props.entry_section = sectionForPath(stored.first.landing_path)
    Object.assign(props, touchProps(stored.last, "last"))
    props.attribution_touch_count = stored.touch_count
    const days = Math.max(0, Math.floor((Date.now() - stored.first_seen_at) / 86_400_000))
    props.days_since_first_touch = days
  }

  const referrerPath = readNavPrevious()
  if (referrerPath) {
    // Clamp to the request-access schema's 255-char `referrer_path` cap so an
    // arbitrarily long prior URL (e.g. a deep 404/resource path) can't overflow
    // the bound and make Zod reject the WHOLE lead — attribution is best-effort
    // metadata, never a reason to drop a submission. (`sectionForPath` is
    // derived, so it stays short regardless.)
    props.referrer_path = truncateChars(referrerPath, 255)
    // Section of the on-site page that referred this conversion — lets the
    // funnel attribute "which section's internal links drive conversions".
    props.referrer_section = sectionForPath(referrerPath)
  }

  return props
}

/**
 * GA4-scoped attribution keys, prioritized. GA4 caps an event at 25 params, and
 * a fully tagged conversion already spends params on its lead/ecommerce dims +
 * `engagement_time_msec` + `page_location` + `event_category`. The FULL 18-key
 * attribution bag can push that event over the cap, at which point GA4 silently
 * drops params — possibly the very attribution this exists to record. This
 * curated subset keeps the high-value channel / section / recency dimensions and
 * omits the verbose, low-report-value ones (`*_term`, `*_content`, the full
 * `*_referrer` URL — the channel is already captured by source/medium/campaign).
 * 12 keys, so even alongside ~8 conversion dims the event stays well under 25.
 */
const GA4_ATTRIBUTION_KEYS: readonly string[] = [
  "first_touch_source",
  "first_touch_medium",
  "first_touch_campaign",
  "first_landing_path",
  "entry_section",
  "last_touch_source",
  "last_touch_medium",
  "last_touch_campaign",
  "attribution_touch_count",
  "days_since_first_touch",
  "referrer_path",
  "referrer_section",
]

/**
 * The curated {@link GA4_ATTRIBUTION_KEYS} subset of {@link getAttributionProperties},
 * for merging into a GA4 event WITHOUT risking the 25-parameter cap. Use this for
 * the GA4 half of a conversion; keep `getAttributionProperties()` (the full bag)
 * for the Vercel / CRM halves, which have no per-event param ceiling.
 *
 * String values are clamped to GA4's 100-char value cap ({@link MAX_PROP_LEN}).
 * Most are already short, but `referrer_path` is deliberately kept up to 255 in
 * the full bag (for the lead schema / CRM), so it must be re-clamped here or GA4
 * would truncate/drop the over-limit dimension.
 */
export function getAttributionPropertiesForGa4(): AttributionProps {
  const full = getAttributionProperties()
  const out: AttributionProps = {}
  for (const key of GA4_ATTRIBUTION_KEYS) {
    const value = full[key]
    if (value === undefined) continue
    out[key] = typeof value === "string" ? clamp(value) : value
  }
  return out
}

/**
 * Purge all persisted attribution (cross-session touch record + the in-session
 * nav pair). Called on an analytics opt-out so previously-captured acquisition
 * data can't linger and reach a sink after consent is withdrawn.
 */
export function clearStoredAttribution(): void {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(ATTRIBUTION_NAV_KEY)
  } catch {
    // ignore
  }
  if (typeof document !== "undefined") {
    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`
  }
}

/** High-value dimensions, in priority order, for the bounded checkout snapshot. */
const SNAPSHOT_KEYS = [
  "first_touch_source",
  "first_touch_medium",
  "first_touch_campaign",
  "referrer_path",
  "entry_section",
  "referrer_section",
  "last_touch_source",
  "last_touch_medium",
  "last_touch_campaign",
  "first_landing_path",
  "attribution_touch_count",
  "days_since_first_touch",
] as const

/**
 * A compact, length-bounded JSON snapshot of the current attribution bag for
 * carrying through Stripe Checkout metadata to the server-side `purchase`
 * (mirrors `ga_client_id`) — so a purchase recovered by the webhook when the
 * client beacon is blocked still attributes to its source. Consent-gated
 * (via `getAttributionProperties`), capped to `maxLen` (Stripe metadata values
 * are limited to 500 chars) by dropping the lowest-priority keys, and
 * `undefined` when there's nothing to send.
 */
export function attributionSnapshotForCheckout(maxLen = 480): string | undefined {
  const props = getAttributionProperties() // {} when opted out / no data
  const out: Record<string, string | number> = {}
  for (const key of SNAPSHOT_KEYS) {
    const value = props[key]
    if (value === undefined) continue
    // Clamp strings to the server/GA4 value cap FIRST, then measure: the bag
    // keeps `referrer_path` up to 255 for the CRM/lead schema, but the server
    // (`parseAttributionSnapshot`) truncates every value to 100 anyway — so
    // spending Stripe's budget on the un-clamped length would needlessly drop
    // lower-priority keys (entry_section, first_landing_path, …) that would
    // otherwise fit. Store what the server will actually keep.
    const clamped = typeof value === "string" ? clamp(value) : value
    // Add the key only if the serialized result stays within the cap.
    if (JSON.stringify({ ...out, [key]: clamped }).length > maxLen) break
    out[key] = clamped
  }
  const json = JSON.stringify(out)
  return json === "{}" ? undefined : json
}

/** O(1) membership set of the only keys a parsed snapshot may carry forward. */
const SNAPSHOT_KEY_SET: ReadonlySet<string> = new Set(SNAPSHOT_KEYS)

/**
 * Server-safe parse of the checkout attribution snapshot back into a flat
 * property bag, defensively (junk / oversized / wrong-typed values are dropped,
 * strings clamped) — Stripe metadata is attacker-influenceable, so never trust
 * its shape. No `window` use; safe to import server-side.
 *
 * **Allow-list, not pass-through.** Only the known {@link SNAPSHOT_KEYS} survive,
 * so a crafted checkout body can't pack arbitrary/invalid parameter names into
 * the 500-char metadata cap and push the downstream GA4 Measurement-Protocol
 * `purchase` over its 25-parameter limit (which would make GA4 drop params —
 * including the authoritative revenue/attribution this fallback exists to carry).
 */
export function parseAttributionSnapshot(
  raw: string | undefined | null,
): Record<string, string | number> {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: Record<string, string | number> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (!SNAPSHOT_KEY_SET.has(key)) continue // drop unknown/crafted keys
      if (typeof value === "string") out[key] = truncateChars(value, MAX_PROP_LEN)
      else if (typeof value === "number" && Number.isFinite(value)) out[key] = value
    }
    return out
  } catch {
    return {}
  }
}
